import apiClient from './apiClient'

const SUBSCRIPTION_CACHE_KEY = 'subscription_check'
const APP_USER_ID_KEY = 'app_user_id'
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000 // 24 hours

class SubscriptionService {
  /**
   * Get the app-specific user ID from /users/me endpoint
   * @returns {Promise<{success: boolean, userId?: string, error?: string}>}
   */
  async getUserId() {
    try {
      // Check if we have cached user ID
      const cachedUserId = localStorage.getItem(APP_USER_ID_KEY)
      if (cachedUserId) {
        return { success: true, userId: cachedUserId }
      }

      // Fetch from API
      const response = await apiClient.get('/api/v1/users/me')

      if (!response) {
        return {
          success: false,
          error: 'Empty response from /users/me endpoint',
        }
      }

      if (response.user_id) {
        // Cache the user ID
        localStorage.setItem(APP_USER_ID_KEY, response.user_id)
        return { success: true, userId: response.user_id }
      }

      // Also check for 'id' field as fallback
      if (response.id) {
        localStorage.setItem(APP_USER_ID_KEY, response.id)
        return { success: true, userId: response.id }
      }

      return {
        success: false,
        error: 'No user ID found in API response',
      }
    } catch (error) {
      console.error('Failed to get user ID:', error)
      return {
        success: false,
        error: error.message || 'Failed to get user ID',
      }
    }
  }

  /**
   * Check if subscription status is cached and still valid (< 24 hours old)
   * @returns {Object|null} Cached subscription data or null if expired/missing
   */
  getCachedSubscriptionStatus() {
    try {
      const cached = localStorage.getItem(SUBSCRIPTION_CACHE_KEY)
      if (!cached) return null

      const data = JSON.parse(cached)
      const now = Date.now()

      // Check if cache is still valid (< 24 hours)
      if (now - data.lastChecked < CACHE_DURATION_MS) {
        return {
          hasActiveSubscription: data.hasActiveSubscription,
          lastChecked: data.lastChecked,
        }
      }

      // Cache expired
      return null
    } catch (error) {
      console.error('Error reading subscription cache:', error)
      return null
    }
  }

  /**
   * Cache subscription status in localStorage
   * @param {boolean} hasActiveSubscription - Whether user has active subscription
   */
  cacheSubscriptionStatus(hasActiveSubscription) {
    try {
      const data = {
        hasActiveSubscription,
        lastChecked: Date.now(),
      }
      localStorage.setItem(SUBSCRIPTION_CACHE_KEY, JSON.stringify(data))
    } catch (error) {
      console.error('Error caching subscription status:', error)
    }
  }

  /**
   * Clear subscription cache (useful after payment completion)
   */
  clearSubscriptionCache() {
    localStorage.removeItem(SUBSCRIPTION_CACHE_KEY)
  }

  /**
   * Check user's subscription status from backend
   * @param {string} userId - App-specific user ID
   * @returns {Promise<{success: boolean, hasActiveSubscription?: boolean, error?: string}>}
   */
  async checkSubscription(userId) {
    try {
      const response = await apiClient.get(`/api/v1/users/${userId}/subscription`)

      // Handle null or empty response
      if (!response) {
        console.error('Empty response from subscription check')
        return {
          success: false,
          error: 'Empty response from subscription endpoint',
          hasActiveSubscription: false, // Fail closed
        }
      }

      // Check if subscription is active
      // API returns { status: "active" | "inactive" | "canceled" | ... }
      const hasActiveSubscription = response.status === 'active'

      console.log('Subscription status from API:', response.status, '-> hasActiveSubscription:', hasActiveSubscription)

      // Cache the result
      this.cacheSubscriptionStatus(hasActiveSubscription)

      return {
        success: true,
        hasActiveSubscription,
      }
    } catch (error) {
      console.error('Subscription check failed:', error)

      // On error, fail closed (block access) for security
      return {
        success: false,
        error: error.message || 'Failed to check subscription',
        hasActiveSubscription: false, // Fail closed
      }
    }
  }

  /**
   * Get subscription status (with caching)
   * Checks cache first, then API if cache is expired
   * @param {boolean} forceRefresh - If true, bypass cache and fetch from API
   * @returns {Promise<{success: boolean, hasActiveSubscription: boolean, error?: string}>}
   */
  async getSubscriptionStatus(forceRefresh = false) {
    try {
      // Check cache first (unless forceRefresh is true)
      if (!forceRefresh) {
        const cached = this.getCachedSubscriptionStatus()
        if (cached) {
          console.log('Using cached subscription status:', cached)
          return {
            success: true,
            hasActiveSubscription: cached.hasActiveSubscription,
          }
        }
      } else {
        console.log('Force refresh: bypassing cache')
      }

      // Get user ID
      const userIdResult = await this.getUserId()
      if (!userIdResult.success) {
        return {
          success: false,
          error: userIdResult.error,
          hasActiveSubscription: false, // Fail closed
        }
      }

      // Check subscription from API
      return await this.checkSubscription(userIdResult.userId)
    } catch (error) {
      console.error('Error getting subscription status:', error)
      return {
        success: false,
        error: error.message || 'Failed to get subscription status',
        hasActiveSubscription: false, // Fail closed
      }
    }
  }

  /**
   * Create Stripe checkout session
   * @returns {Promise<{success: boolean, checkoutUrl?: string, sessionId?: string, error?: string}>}
   */
  async createCheckoutSession() {
    try {
      // Get user ID
      const userIdResult = await this.getUserId()
      if (!userIdResult.success) {
        return {
          success: false,
          error: userIdResult.error || 'Failed to get user ID',
        }
      }

      const response = await apiClient.post(`/api/v1/users/${userIdResult.userId}/subscription`)

      if (!response) {
        return {
          success: false,
          error: 'Empty response from checkout session API',
        }
      }

      if (response.checkout_url) {
        return {
          success: true,
          checkoutUrl: response.checkout_url,
          sessionId: response.session_id,
        }
      }

      return {
        success: false,
        error: 'No checkout URL returned from API',
      }
    } catch (error) {
      console.error('Failed to create checkout session:', error)
      return {
        success: false,
        error: error.message || 'Failed to create checkout session',
      }
    }
  }

  /**
   * Redirect to Stripe checkout
   * @returns {Promise<void>}
   */
  async redirectToCheckout() {
    const result = await this.createCheckoutSession()

    if (result.success && result.checkoutUrl) {
      // Redirect to Stripe
      window.location.href = result.checkoutUrl
    } else {
      throw new Error(result.error || 'Failed to create checkout session')
    }
  }
}

export default new SubscriptionService()
